import { alpha } from '@mui/system';
import { themeObject } from '../../../../lib';
import { AttributeAssociationItem } from '../../edge-definition-helper';
import { AttributeAssociationSelectionProps } from '../create-edge-definition/create-edge-definition-helper';

export interface EditAttributeAssociationSelectionProps
    extends Omit<
        AttributeAssociationSelectionProps,
        'onAttributeAssociationChanged' | 'isFirstItem'
    > {
    handleAddItem: (currentItem: AttributeAssociationItem) => void;
    isFirstItem: boolean;
}

export interface EditAttributeAssociationWidgetProps {
    attributeAssociationList: AttributeAssociationItem[];
    onAttributeAssociationListChange: (
        newList: AttributeAssociationItem[]
    ) => void;
    leftSideSelectionOptions: string[];
    rightSideSelectionOptions: string[];
}

export interface SelectedAttributeAssociationProps {
    item: AttributeAssociationItem;
    onDelete: (item: AttributeAssociationItem) => void;
}

export const updateEdgeDefinitionStyles = {
    generalInfoContainer: {
        backgroundColor: themeObject.palette.background.default,
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        borderRadius: '5px',
        height: '170px',
        mt: 2,
        py: 3.5,
        pl: 3.875,
    },
    attributeAssociationsContainer: {
        width: '100%',
        mt: 2.5,
    },
    sectionText: {
        ...themeObject.typography.h2,
        fontSize: '26px',
        lineHeight: '30.52px',
        color: themeObject.palette.primary[700],
        ml: '5px',
    },
    bodyContainer: {
        backgroundColor: themeObject.palette.background.default,
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        minHeight: '450px',
        borderRadius: '10px',
        mt: 1.5,
    },
    infoDisplayContainer: {
        width: '100%',
    },
    fromDictionaryContainer: {
        minWidth: '200px',
    },
    toDictionaryContainer: {
        minWidth: '200px',
        ml: 2,
    },
    dictionaryInfoContainer: {
        display: 'flex',
        flexDirection: 'row',
        mt: 2,
    },
    sectionLabel: {
        ...themeObject.typography.Components.inputLabel,
        color: themeObject.palette.grayScale[800],
        lineHeight: '21.17px',
        letterSpacing: '0.15px',
        mb: '0px',
    },
    sectionValue: {
        ...themeObject.typography.Components.inputText,
        color: themeObject.palette.grayScale[800],
        lineHeight: '24px',
        wordBreak: 'break-word',
        mt: '4px',
    },
};

export const editAttributeAssociationWidgetStyles = {
    mainContainer: {
        pt: 4,
        px: 4,
    },
    fabButton: {
        borderRadius: '20px',
        width: '288px',
        height: '40.25px',
        '& .MuiTypography-root': {
            fontFamily: 'Red Hat Text',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '24px',
            color: themeObject.palette.grayScale[700],
            ml: '2px',
        },
    },
    selectedAssociationContainer: {
        mt: 2.75,
    },
};

export const editAttributeAssociationSelectionStyles = {
    card: {
        height: '100%',
        elevation: 2,
        display: 'flex',
    },
    attributeAssociationSelectionContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        my: 2,
        ml: 2.5,
        mr: 2,
    },
    associationLeftSideContainer: {
        height: '100%',
        width: '100%',
        display: 'flex',
        alignSelf: 'flex-start',
    },
    associationAutocomplete: {
        width: '100%',
        '& .MuiFormControl-root': {
            width: '100%',
        },
        '& .MuiInputBase-root': {
            height: '38px',
            '& .MuiInputBase-input': {
                height: '38px',
            },
        },
    },
    associationRightSideContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignSelf: 'flex-start',
        ml: 2.5,
    },
    buttonsContainer: {
        display: 'flex',
        alignItems: 'baseline',
    },
    associationAddButtonContainer: {
        mx: 2.5,
    },
    associationAddButton: {
        width: '97px',
        height: '40px',
        mx: 2.5,
        mt: 2.875,
    },
    associationDeleteButtonContainer: {
        height: '100%',
    },
    associationDeleteButton: {
        width: '24px',
        height: '24px',
        backgroundColor: themeObject.palette.grayScale[500],
        color: themeObject.palette.background.default,
        mt: 2.5,
    },
};

export const selectedAttributeAssociationStyles = {
    mainContainer: {
        height: '50px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1.5,
        elevation: 2,
    },
    valueText: {
        ...themeObject.typography.body1,
        color: themeObject.palette.grayScale[800],
    },
    fromTextContainer: {
        ml: 2.5,
        my: 1.8125,
        width: '100%',
    },
    toTextContainer: {
        my: 1.8125,
        width: '100%',
    },
    menuContainer: {
        mr: 2,
        my: 1,
    },
    menu: {
        '& .MuiPaper-root': {
            width: '166px',
            height: '71px',
            borderRadius: '12px',
            boxShadow: `
            0px 3px 14px 2px ${alpha(themeObject.palette.grayScale[700], 0.12)},
            0px 8px 10px 1px ${alpha(themeObject.palette.grayScale[700], 0.14)},
            0px 5px 5px -3px ${alpha(themeObject.palette.grayScale[700], 0.2)},`,
            '& .MuiList-root': {
                my: '8px',
                '& .MuiButtonBase-root': {
                    width: '100%',
                },
            },
        },
    },
};
